import { mockNotFoundError, mockValidationError } from "../mockDomainError";
import type {
  CreateStoryRequest,
  CreateStoryResponse,
  ListStoriesQuery,
  StoriesListResponseApi,
  StoryListItemApi,
  StoryMetaApi,
  StoryPublishStatus,
  UpdateStoryMetaRequest,
} from "./storiesApi.types";

const MOCK_LATENCY_MS = 200;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

let mockStoryIdCounter = 2;

// ─── Story list ───────────────────────────────────────────────────────────────

// Deliberately unsorted (recency order: 003, 001, 004, 002) so consumers'
// updated_at-desc sorting is actually exercised by the fixture.
const MOCK_STORY_LIST_ITEMS: StoryListItemApi[] = [
  {
    id: "mock-story-uuid-001",
    workspace_id: "mock-workspace-uuid-001",
    tenant_id: "mock-tenant-uuid-001",
    title: "DrugX launch performance",
    status: "ready",
    created_by: "mock-user-uuid-001",
    created_at: "2026-07-10T09:15:00.000000Z",
    updated_at: "2026-07-12T10:30:00.000000Z",
  },
  {
    id: "mock-story-uuid-002",
    workspace_id: "mock-workspace-uuid-001",
    tenant_id: "mock-tenant-uuid-001",
    title: null,
    status: "pending",
    created_by: "mock-user-uuid-002",
    created_at: "2026-07-08T14:00:00.000000Z",
    updated_at: "2026-07-08T14:00:00.000000Z",
  },
  {
    id: "mock-story-uuid-003",
    workspace_id: "mock-workspace-uuid-001",
    tenant_id: "mock-tenant-uuid-001",
    title: "Coverage levels and prescription",
    status: "generating",
    created_by: "mock-user-uuid-001",
    created_at: "2026-07-11T08:00:00.000000Z",
    updated_at: "2026-07-13T06:40:02.773394Z",
  },
  {
    id: "mock-story-uuid-004",
    workspace_id: "mock-workspace-uuid-001",
    tenant_id: "mock-tenant-uuid-001",
    title: "Regional uptake trends",
    status: "failed",
    created_by: "mock-user-uuid-003",
    created_at: "2026-07-09T11:30:00.000000Z",
    updated_at: "2026-07-10T16:45:00.000000Z",
  },
];

// Mirrors the backend's own defaults (app/api/v1/stories.py) so mock mode and
// live mode page identically — an infinite scroller that stops on
// `offset + items.length >= total` must behave the same against both.
const MOCK_DEFAULT_LIMIT = 50;

export const mockListStories = async (
  workspaceId: string,
  query?: ListStoriesQuery,
): Promise<StoriesListResponseApi> => {
  await wait(MOCK_LATENCY_MS);
  const limit = query?.limit ?? MOCK_DEFAULT_LIMIT;
  const offset = query?.offset ?? 0;
  // Mirrors the backend contract for `q` / `exclude_story_id` so the mock
  // stands in for the server-side filter (see ListStoriesQuery).
  const q = query?.q?.trim().toLowerCase() ?? "";
  const all = MOCK_STORY_LIST_ITEMS.filter(
    (item) =>
      item.id !== query?.exclude_story_id &&
      (!q || (item.title ?? "").toLowerCase().includes(q)),
  ).map((item) => ({
    ...item,
    workspace_id: workspaceId,
  }));
  return {
    items: all.slice(offset, offset + limit),
    total: all.length,
    limit,
    offset,
  };
};

// ─── Story row ────────────────────────────────────────────────────────────────
// One in-memory store behind every story read and write. An entry is seeded
// either by mockCreateStory (status pending, so the build page walks the
// pipeline locally) or on first read (status ready: with REACT_APP_STORIES_API
// live, the story was created by the real backend and the page should open at
// once). The workspace it was seeded under owns it; any other workspace gets
// the backend's 404 for a story that "does not belong to workspace_id".

const MOCK_STORY_META = new Map<string, StoryMetaApi>();

const MOCK_STORY_META_CREATED_AT = "2026-09-28T00:00:00.000000Z";

const seedStoryMeta = (
  workspaceId: string,
  storyId: string,
  over: Partial<StoryMetaApi> = {},
): StoryMetaApi => {
  const seeded: StoryMetaApi = {
    id: storyId,
    workspace_id: workspaceId,
    tenant_id: "mock-tenant-uuid-001",
    title: "Untitled Story",
    description: null,
    status: "ready",
    publish_status: "draft",
    is_auto_refresh: false,
    input_prompt: null,
    has_files: false,
    created_by: "mock-user-uuid-001",
    created_at: MOCK_STORY_META_CREATED_AT,
    updated_at: MOCK_STORY_META_CREATED_AT,
    ...over,
  };
  MOCK_STORY_META.set(storyId, seeded);
  return seeded;
};

const resolveStoryMeta = (
  workspaceId: string,
  storyId: string,
): StoryMetaApi => {
  const existing = MOCK_STORY_META.get(storyId);
  if (!existing) return seedStoryMeta(workspaceId, storyId);
  // Same text as magicbi_core's NotFoundError("Story", story_id).
  if (existing.workspace_id !== workspaceId) {
    throw mockNotFoundError(`Story not found: ${storyId}`);
  }
  return existing;
};

// Simulates the pipeline: a created story advances pending → generating →
// ready across successive reads, and a refresh returns to ready the same way,
// so the build page reaches the canvas offline.
const POLLS_UNTIL_READY = 3;
const pollCounts = new Map<string, number>();

const advanceStatus = (meta: StoryMetaApi): void => {
  const count = (pollCounts.get(meta.id) ?? 0) + 1;
  if (count >= POLLS_UNTIL_READY) {
    pollCounts.delete(meta.id);
    meta.status = "ready";
    return;
  }
  pollCounts.set(meta.id, count);
  if (meta.status !== "refreshing") {
    meta.status = count === 1 ? "pending" : "generating";
  }
};

export const mockCreateStory = async (
  workspaceId: string,
  input: CreateStoryRequest,
): Promise<CreateStoryResponse> => {
  await wait(MOCK_LATENCY_MS);
  const id = `mock-story-uuid-00${mockStoryIdCounter++}`;

  // A new-path blank canvas, as api-service creates it: ready at once (there is
  // no pipeline to wait on), on the v2 canvas, and with no plan to review.
  if (input.creation_method === "blank") {
    seedStoryMeta(workspaceId, id, {
      ...(input.title?.trim() ? { title: input.title.trim() } : {}),
      status: "ready",
      editable: true,
      new_path: true,
    });
    return { story_id: id, story_plan_id: null };
  }

  const planId = `mock-plan-uuid-00${mockStoryIdCounter - 1}`;
  seedStoryMeta(workspaceId, id, {
    status: "pending",
    input_prompt: input.prompt ?? null,
    has_files: (input.files?.length ?? 0) > 0,
  });
  return { story_id: id, story_plan_id: planId };
};

/** StoryUpdateRequest limits on doc-hub `main`. */
const STORY_TITLE_MAX_LENGTH = 255;
const STORY_DESCRIPTION_MAX_LENGTH = 2000;

/**
 * api-service answers every request-validation failure with this one message
 * (src/entrypoints/error_handlers.py); the field-level reason is only in
 * `details`, in pydantic's shape.
 */
const REQUEST_VALIDATION_MESSAGE = "Request validation failed";

const fieldValidationError = (field: string, type: string, msg: string) =>
  mockValidationError(REQUEST_VALIDATION_MESSAGE, [
    { loc: ["body", field], type, msg },
  ]);

/** The server's StoryUpdateRequest rules, so mock mode reaches the 422 path. */
const validateStoryMetaUpdate = (input: UpdateStoryMetaRequest): void => {
  const { title, description, is_auto_refresh } = input;
  if (
    title === undefined &&
    description === undefined &&
    is_auto_refresh === undefined &&
    !("palette" in input)
  ) {
    throw mockValidationError(REQUEST_VALIDATION_MESSAGE);
  }
  if (title !== undefined && title.length === 0) {
    throw fieldValidationError(
      "title",
      "string_too_short",
      "String should have at least 1 character",
    );
  }
  if (title !== undefined && title.length > STORY_TITLE_MAX_LENGTH) {
    throw fieldValidationError(
      "title",
      "string_too_long",
      `String should have at most ${STORY_TITLE_MAX_LENGTH} characters`,
    );
  }
  if (
    description !== undefined &&
    description.length > STORY_DESCRIPTION_MAX_LENGTH
  ) {
    throw fieldValidationError(
      "description",
      "string_too_long",
      `String should have at most ${STORY_DESCRIPTION_MAX_LENGTH} characters`,
    );
  }
};

// Every function returns a copy so a caller mutating its result (a React Query
// cache, a test) cannot reach into the store.

/**
 * Mirrors the backend: opening an auto-refresh story starts a refresh, but a
 * `?poll=true` read never does — otherwise the build page's 5s poll would
 * restart it on every tick after the story came back to ready.
 */
export const mockGetStoryMeta = async (
  workspaceId: string,
  storyId: string,
  poll = false,
): Promise<StoryMetaApi> => {
  await wait(MOCK_LATENCY_MS);
  const meta = resolveStoryMeta(workspaceId, storyId);
  if (!poll && meta.is_auto_refresh && meta.status === "ready") {
    meta.status = "refreshing";
  } else if (meta.status !== "ready" && meta.status !== "failed") {
    advanceStatus(meta);
  }
  return { ...meta };
};

export const mockUpdateStoryMeta = async (
  workspaceId: string,
  storyId: string,
  input: UpdateStoryMetaRequest,
): Promise<StoryMetaApi> => {
  await wait(MOCK_LATENCY_MS);
  const meta = resolveStoryMeta(workspaceId, storyId);
  validateStoryMetaUpdate(input);
  // Field by field, so an explicit `undefined` in the body leaves the stored
  // value alone instead of erasing it.
  if (input.title !== undefined) meta.title = input.title;
  if (input.description !== undefined) meta.description = input.description;
  if ("palette" in input && input.palette !== undefined) meta.palette = input.palette;
  // Only the flag is persisted here; the refresh itself starts on the next
  // plain GET (story open), as on the real backend.
  if (input.is_auto_refresh !== undefined) {
    meta.is_auto_refresh = input.is_auto_refresh;
  }
  meta.updated_at = new Date().toISOString();
  return { ...meta };
};

/**
 * Backs both publish and unpublish. Idempotent like the server: re-applying
 * the current state returns the story untouched, `updated_at` included.
 */
export const mockSetStoryPublishStatus = async (
  workspaceId: string,
  storyId: string,
  publishStatus: StoryPublishStatus,
): Promise<StoryMetaApi> => {
  await wait(MOCK_LATENCY_MS);
  const meta = resolveStoryMeta(workspaceId, storyId);
  if (meta.publish_status !== publishStatus) {
    meta.publish_status = publishStatus;
    meta.updated_at = new Date().toISOString();
  }
  return { ...meta };
};

/** Mock of the duplicate route: a draft copy with a new id. */
export const mockDuplicateStory = async (
  workspaceId: string,
  storyId: string,
): Promise<StoryMetaApi> => {
  await wait(MOCK_LATENCY_MS);
  const source = resolveStoryMeta(workspaceId, storyId);
  mockStoryIdCounter += 1;
  const now = new Date().toISOString();
  const copy: StoryMetaApi = {
    ...source,
    id: `mock-story-uuid-${String(mockStoryIdCounter).padStart(3, "0")}`,
    title: `${source.title} (copy)`,
    publish_status: "draft",
    created_at: now,
    updated_at: now,
  };
  MOCK_STORY_META.set(copy.id, copy);
  return { ...copy };
};

// ─── Test helpers ─────────────────────────────────────────────────────────────

export const __MOCK_STORY_LIST_ITEMS_FOR_TESTS__: StoryListItemApi[] =
  MOCK_STORY_LIST_ITEMS;

export const __resetMockStoriesForTests__ = (): void => {
  mockStoryIdCounter = 2;
  pollCounts.clear();
  MOCK_STORY_META.clear();
};
